import { placeableSegments } from '@src/editor/dubbing/DubPlacementService';
import { dubEndMs, segmentStartMs } from '@src/editor/dubbing/dubWordTimeline';
import { LogManager } from '@src/utility/Logger';

import type { ResolvedDubManifest } from '@src/repositories/DubbingRepository';

const logger = LogManager.getLogger('aiVoices:composeDubTrack');

/**
 * Compose the dub segments into a single story-length audio buffer (PLAT-10597,
 * DE4). Renditions are per-*unit* WAVs; each manifest segment is the slice
 * `[renditionOffsetMs, +renditionDurationMs)` of its unit, placed on the story
 * timeline at `placedStartMs` (PLAT-10713 Slide; `originalStartMs` fallback for
 * pre-Slide manifests). We decode each distinct unit once, then copy
 * every segment's slice into one mono buffer (silence in the gaps) — the "single
 * WAV" the player streams while the original video plays muted.
 *
 * Returns `null` when nothing decodes (no playable dub).
 *
 * PLAT-11906 — with `placedLinesOnly`, the lines mixed are the ones the clip
 * audio places (`placeableSegments`): a superseded copy of a line is not played
 * on top of its newer recording, and a line with no signed address is left
 * out, both reported. The Translation Review asks for this, because its
 * captions and highlights show only those lines; without it every stored line
 * is mixed, as before.
 */
export async function composeDubTrack(
  ctx: AudioContext,
  resolved: ResolvedDubManifest,
  signal?: AbortSignal,
  options: { placedLinesOnly?: boolean } = {},
): Promise<AudioBuffer | null> {
  const { manifest, renditionUrls } = resolved;
  if (!manifest.segments.length) {
    logger.debug('composeDubTrack: manifest has no segments');
    return null;
  }
  let segments = manifest.segments;
  if (options.placedLinesOnly) {
    const { live, skipped } = placeableSegments(manifest, renditionUrls);
    if (skipped.length > 0) {
      logger.warn('Dub lines left out of the track', { skipped });
    }
    segments = live;
  }

  // Decode each distinct rendition WAV once (units are shared across segments).
  const distinctIds = Array.from(new Set(segments.map(s => s.renditionId)));
  logger.debug('composeDubTrack: start', {
    segments: segments.length,
    distinctRenditions: distinctIds.length,
    signedUrls: Object.keys(renditionUrls).length,
  });
  const unitBuffers = new Map<string, AudioBuffer>();
  await Promise.all(
    distinctIds.map(async id => {
      const url = renditionUrls[id];
      if (!url) {
        // Manifest references a rendition with no signed URL — the read side
        // couldn't sign it (missing/mis-keyed S3 object). Surfaced so a
        // silently-empty dub track is diagnosable (no-silent-failures rule).
        logger.warn('Rendition has no signed URL', { renditionId: id });
        return;
      }
      let res: Response;
      try {
        res = await fetch(url, { signal });
      } catch (err) {
        // Network/CORS failure fetching the signed S3 rendition URL.
        logger.warn('Rendition fetch threw', { renditionId: id, err });
        return;
      }
      if (!res.ok) {
        // Non-2xx from S3 (expired signature, missing object, access denied).
        logger.warn('Rendition fetch not ok', {
          renditionId: id,
          status: res.status,
        });
        return;
      }
      const bytes = await res.arrayBuffer();
      try {
        const decoded = await ctx.decodeAudioData(bytes);
        unitBuffers.set(id, decoded);
        logger.debug('composeDubTrack: rendition decoded', {
          renditionId: id,
          bytes: bytes.byteLength,
          durationSec: decoded.duration,
          sampleRate: decoded.sampleRate,
        });
      } catch (err) {
        // Skip an undecodable unit rather than failing the whole track.
        logger.warn('Rendition decode failed', {
          renditionId: id,
          bytes: bytes.byteLength,
          err,
        });
      }
    }),
  );
  if (unitBuffers.size === 0) {
    logger.warn('No renditions decoded — dub track is empty', {
      distinctRenditions: distinctIds.length,
      signedUrls: Object.keys(renditionUrls).length,
    });
    return null;
  }

  const { sampleRate } = ctx;
  // Placed start on the story timeline: Slide's `placedStartMs` (PLAT-10713),
  // or `originalStartMs` for pre-Slide manifests — the one shared rule.
  const startOf = segmentStartMs;
  // Total length = the latest segment end on the story timeline (+1 frame).
  const endMs = dubEndMs(segments);
  const totalFrames = Math.max(1, Math.ceil((endMs / 1000) * sampleRate) + 1);
  // A single story-length mono buffer. Bounded by the story's dubbed span; for
  // very long stories this can be large (≈4 bytes/frame) — logged so an
  // outsized composition is visible in diagnostics.
  logger.debug('Composing dub track', {
    segments: segments.length,
    units: unitBuffers.size,
    durationMs: endMs,
    frames: totalFrames,
  });
  const out = ctx.createBuffer(1, totalFrames, sampleRate);
  const outData = out.getChannelData(0);

  // Segments never overlap: Slide places each at `max(originalStart, prevEnd)`
  // and fit-or-shrink keeps each within its slot — either way starts are
  // monotonic and non-overlapping — so a plain copy (not additive mix) is
  // correct and leaves the inter-segment gaps as silence.
  for (const seg of segments) {
    const unit = unitBuffers.get(seg.renditionId);
    if (!unit) continue;
    const src = unit.getChannelData(0); // renditions are mono PCM
    const srcStart = Math.floor((seg.renditionOffsetMs / 1000) * sampleRate);
    const sliceLen = Math.floor((seg.renditionDurationMs / 1000) * sampleRate);
    const dstStart = Math.floor((startOf(seg) / 1000) * sampleRate);
    for (let i = 0; i < sliceLen; i++) {
      const s = srcStart + i;
      const d = dstStart + i;
      if (s >= src.length || d >= outData.length) break;
      outData[d] = src[s];
    }
  }
  logger.debug('composeDubTrack: done', {
    units: unitBuffers.size,
    frames: totalFrames,
    durationSec: out.duration,
  });
  return out;
}
